import { Data, Effect } from 'effect';
import { okraApi } from '../okraApi';
import type {
  TableListItem,
  TableSchemaResponse,
  CreateTableBody,
  UpdateTableBody,
  WorkflowRunResponse,
  InvoiceExtractionQuality,
  WorkflowRunListResponse,
  InvoiceExtractionExceptionListResponse,
} from '../types';

export class TablesEndpointError extends Data.TaggedError('TablesEndpointError')<{
  readonly operation: 'table-url' | 'workflow-url';
}> {}

const tablesEndpointCauses = new WeakMap<TablesEndpointError, unknown>();

function tablesEndpointFailure(
  operation: TablesEndpointError['operation'],
  cause: unknown,
): TablesEndpointError {
  const error = new TablesEndpointError({ operation });
  tablesEndpointCauses.set(error, cause);
  return error;
}

export function tablesEndpointErrorCause(error: TablesEndpointError): unknown {
  return tablesEndpointCauses.get(error);
}

export const tableResourceUrlEffect = Effect.fn('web.api.tables.resourceUrl')(function* (
  id: string,
  suffix = '',
) {
  return yield* Effect.try({
    try: () => `/v1/tables/${id}${suffix}`,
    catch: (cause) => tablesEndpointFailure('table-url', cause),
  });
});

export const invoiceExtractionRunUrlEffect = Effect.fn('web.api.tables.invoiceRunUrl')(function* (
  runId: string,
  suffix = '',
) {
  return yield* Effect.try({
    try: () =>
      `/v1/workflows/invoice-extraction/runs/${encodeURIComponent(runId)}${suffix}`,
    catch: (cause) => tablesEndpointFailure('workflow-url', cause),
  });
});

function tableResourceUrl(id: string, suffix = ''): string {
  return Effect.runSync(tableResourceUrlEffect(id, suffix).pipe(Effect.orDie));
}

function invoiceExtractionRunUrl(runId: string, suffix = ''): string {
  return Effect.runSync(invoiceExtractionRunUrlEffect(runId, suffix).pipe(Effect.orDie));
}

export const tablesEndpoints = okraApi.injectEndpoints({
  endpoints: (build) => ({
    listTables: build.query<{ data: TableListItem[] }, void>({
      query: () => '/v1/tables',
      providesTags: ['Tables'],
    }),
    createTable: build.mutation<
      TableSchemaResponse,
      CreateTableBody
    >({
      query: (body) => ({ url: '/v1/tables', method: 'POST', body }),
      invalidatesTags: ['Tables'],
    }),
    getTableSchema: build.query<TableSchemaResponse, string>({
      query: (id) => tableResourceUrl(id),
      providesTags: (_r, _e, id) => [{ type: 'Tables', id }],
    }),
    updateTable: build.mutation<
      TableSchemaResponse,
      UpdateTableBody
    >({
      query: ({ tableId, name, template, columns }) => {
        const body: Record<string, unknown> = {};
        if (name !== undefined) body.name = name;
        if (template !== undefined) body.template = template;
        if (columns !== undefined) body.columns = columns;
        return {
          url: tableResourceUrl(tableId),
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: (_r, _e, { tableId }) => [
        { type: 'Tables', id: tableId },
        { type: 'TableRows', id: tableId },
        'Tables',
      ],
    }),
    getTableRows: build.query<{ total: number; rows: Record<string, unknown>[] }, string>({
      query: (id) => tableResourceUrl(id, '/rows'),
      providesTags: (_r, _e, id) => [{ type: 'TableRows', id }],
    }),
    ingestTable: build.mutation<
      WorkflowRunResponse,
      {
        tableId: string;
        inputs: Array<{ file_id: string }>;
        mode?: 'extract' | 'chat';
      }
    >({
      query: ({ tableId, inputs, mode }) => ({
        url: tableResourceUrl(tableId, '/ingest'),
        method: 'POST',
        body: { inputs, ...(mode ? { mode } : {}) },
      }),
      invalidatesTags: (_r, _e, { tableId }) => [{ type: 'TableRows', id: tableId }],
    }),
    runInvoiceExtractionWorkflow: build.mutation<
      WorkflowRunResponse,
      {
        table_id?: string;
        quality?: InvoiceExtractionQuality;
        inputs: Array<{ file_id: string }>;
      }
    >({
      query: (body) => ({
        url: '/v1/workflows/invoice-extraction/runs',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, body) =>
        body.table_id ? [{ type: 'TableRows', id: body.table_id }] : [],
    }),
    listInvoiceExtractionRuns: build.query<WorkflowRunListResponse, void>({
      query: () => '/v1/workflows/invoice-extraction/runs',
      providesTags: ['SessionTurns'],
    }),
    retryInvoiceExtractionRun: build.mutation<WorkflowRunResponse, string>({
      query: (runId) => ({
        url: invoiceExtractionRunUrl(runId, '/retry'),
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, _runId) => ['SessionTurns', 'Tables'],
    }),
    listInvoiceExtractionExceptions: build.query<
      InvoiceExtractionExceptionListResponse,
      string
    >({
      query: (runId) => invoiceExtractionRunUrl(runId, '/exceptions'),
      providesTags: (_r, _e, id) => [{ type: 'SessionTurns', id } as const],
    }),
    resolveInvoiceExtractionException: build.mutation<
      { id: string; run_id: string; table_id: string; row_id: string; status: 'resolved' },
      {
        runId: string;
        exceptionId: string;
        row_id: string;
        field?: string | null;
        value?: unknown;
        action?: 'approve' | 'correct';
      }
    >({
      query: ({ runId, exceptionId, row_id, field, value, action }) => ({
        url: invoiceExtractionRunUrl(runId, `/exceptions/${encodeURIComponent(exceptionId)}/resolve`),
        method: 'POST',
        body: {
          row_id,
          ...(field ? { field } : {}),
          ...(value !== undefined ? { value } : {}),
          ...(action ? { action } : {}),
        },
      }),
      invalidatesTags: (_r, _e, { runId }) => [
        { type: 'SessionTurns', id: runId } as const,
        'SessionTurns',
        'TableRows',
      ],
    }),
    getSessionTurns: build.query<{ data: Array<{ type: string; payload?: Record<string, unknown> }> }, string>({
      query: (sessionId) => `/v1/sessions/${sessionId}/turns`,
      providesTags: (_r, _e, id) => [{ type: 'SessionTurns', id } as const],
    }),
  }),
});

export const {
  useListTablesQuery,
  useCreateTableMutation,
  useGetTableSchemaQuery,
  useUpdateTableMutation,
  useGetTableRowsQuery,
  useIngestTableMutation,
  useRunInvoiceExtractionWorkflowMutation,
  useListInvoiceExtractionRunsQuery,
  useRetryInvoiceExtractionRunMutation,
  useListInvoiceExtractionExceptionsQuery,
  useResolveInvoiceExtractionExceptionMutation,
  useGetSessionTurnsQuery,
} = tablesEndpoints;
